import { storeConfiguration, StorePurchaseError, verifyStoreTransactions } from './store-purchases.js';

let offer: { status: 'closed' } | { status: 'open'; result: Promise<boolean> } = { status: 'closed' };
/** All native Pro entry points use the same store-priced offer. */
export function openNativePro(): Promise<boolean> {
  if (offer.status === 'open') return offer.result;
  const result = showNativePro().finally(() => { offer = { status: 'closed' }; });
  offer = { status: 'open', result };
  return result;
}
async function showNativePro(): Promise<boolean> {
  const dialog = document.createElement('dialog');
  dialog.className = 'native-pro-dialog';
  dialog.setAttribute('aria-labelledby', 'native-pro-title');
  dialog.innerHTML = `<form method="dialog"><button aria-label="Close" class="native-pro-close">×</button></form>
    <h2 id="native-pro-title">Draw on a Map Pro</h2>
    <p>Export maps without a watermark and use Pro map tools.</p>
    <p data-price></p><p data-status role="status">Loading subscription…</p>
    <button data-buy hidden>Subscribe</button>
    <a data-sign-in href="/account/" hidden>Sign in</a>
    <button data-restore>Restore purchases</button><button data-manage>Manage subscription</button>
    <p data-renewal hidden>The subscription renews each month until you cancel it in your store account.</p>
    <p><a href="/terms/">Terms of use</a> · <a href="/privacy/">Privacy policy</a></p>`;
  const style = document.createElement('style');
  style.textContent = `.native-pro-dialog{box-sizing:border-box;width:min(440px,calc(100vw - 32px));max-height:90dvh;overflow:auto;padding:24px;border:1px solid var(--border-color,#ddd);border-radius:16px;background:var(--bg-primary,#fff);color:var(--text-primary,#172033);font:16px/1.5 system-ui}.native-pro-dialog::backdrop{background:#0008}.native-pro-dialog h2{margin:0 32px 16px 0;font-size:24px}.native-pro-dialog button{padding:12px;margin:6px 0;cursor:pointer}.native-pro-dialog [data-buy]{display:block;width:100%;background:#166534;color:white;border:0;border-radius:8px;font:inherit}.native-pro-dialog [hidden]{display:none!important}.native-pro-dialog .native-pro-close{position:absolute;top:8px;right:14px;border:0;background:transparent;color:inherit;font-size:24px}.native-pro-dialog a{color:var(--link-color,#1763af)}`;
  dialog.appendChild(style);
  document.body.appendChild(dialog);
  const status = dialog.querySelector<HTMLElement>('[data-status]')!;
  const buy = dialog.querySelector<HTMLButtonElement>('[data-buy]')!;
  const restore = dialog.querySelector<HTMLButtonElement>('[data-restore]')!;
  const manage = dialog.querySelector<HTMLButtonElement>('[data-manage]')!;
  type OfferState = { status: 'ready' | 'working' | 'active' } | { status: 'closed'; active: boolean };
  let state: OfferState = { status: 'ready' };
  const closed = new Promise<boolean>(resolve => dialog.addEventListener('close', () => {
    const active = state.status === 'active';
    state = { status: 'closed', active }; dialog.remove(); resolve(active);
  }, { once: true }));
  const activate = () => { if (state.status !== 'working') return; state = { status: 'active' }; dialog.close(); };
  dialog.addEventListener('cancel', event => { if (state.status === 'working') event.preventDefault(); });
  dialog.showModal();
  const billing = window.drawOnAMapNative?.billing;
  if (!billing) {
    status.textContent = 'Update the app to buy or restore Pro.';
    restore.disabled = true; manage.disabled = true;
    return closed;
  }
  const run = async (action: () => Promise<void>) => {
    if (state.status !== 'ready') return;
    state = { status: 'working' };
    for (const button of dialog.querySelectorAll('button')) button.disabled = true;
    try { await action(); }
    catch (error) {
      status.textContent = error instanceof Error ? error.message : 'The purchase could not complete.';
      if (error instanceof StorePurchaseError && error.status === 401) dialog.querySelector<HTMLElement>('[data-sign-in]')!.hidden = false;
    } finally {
      if (state.status === 'working') state = { status: 'ready' };
      for (const button of dialog.querySelectorAll('button')) button.disabled = false;
    }
  };
  manage.addEventListener('click', () => { void run(() => billing.manage()); });
  restore.addEventListener('click', () => { void run(async () => {
    await storeConfiguration(billing);
    status.textContent = 'Checking purchases…';
    const result = await billing.restore();
    const active = await verifyStoreTransactions(billing, result.transactions);
    status.textContent = active ? 'Pro is ready.' : 'No active Pro purchase was found for this store account.';
    if (active) activate();
  }); });
  buy.addEventListener('click', () => { void run(async () => {
    const config = await storeConfiguration(billing);
    if (config.active) { activate(); return; }
    // Recover a paid, unfinished transaction before allowing another purchase.
    const current = await billing.current();
    if (await verifyStoreTransactions(billing, current.transactions)) { activate(); return; }
    const result = await billing.purchase(config.accountToken);
    if (result.state === 'cancelled') { status.textContent = 'Purchase cancelled.'; return; }
    if (result.state === 'pending') { status.textContent = 'Your store must approve the purchase before Pro can start.'; return; }
    const active = await verifyStoreTransactions(billing, result.transactions ?? []);
    status.textContent = active ? 'Pro is ready.' : 'No active Pro subscription was found.';
    if (active) activate();
  }); });
  void run(async () => {
    const config = await storeConfiguration(billing);
    if (config.active) { status.textContent = 'You have Pro access.'; return; }
    const product = await billing.catalog();
    if (product.productId !== config.productId || product.period !== 'month') throw new Error('The Pro subscription is unavailable.');
    dialog.querySelector<HTMLElement>('[data-price]')!.textContent = `${product.displayPrice} per month`;
    dialog.querySelector<HTMLElement>('[data-renewal]')!.hidden = false;
    status.textContent = '';
    buy.hidden = false;
  });
  return closed;
}
